import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { roleService } from '@/api/services/roleService'
import { useAuthStore } from '@/store/useAuthStore'

export function useRoles(p?: { page?: number; limit?: number }) {
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useQuery({
    queryKey: ['roles', p, isSchoolAdmin],
    queryFn:  () => isSchoolAdmin ? roleService.school.getRoles(p) : roleService.getRoles(p),
    staleTime: 300_000,
  })
}

export function usePermissions(p?: { page?: number; limit?: number }) {
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useQuery({
    queryKey: ['permissions', p, isSchoolAdmin],
    queryFn:  () => isSchoolAdmin ? roleService.school.getPermissions(p) : roleService.getPermissions(p),
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  })
}

// Every permission across all pages — for the role-mapping checkbox grid.
export function useAllPermissions(enabled = true) {
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useQuery({
    enabled,
    queryKey: ['permissions', 'all', isSchoolAdmin],
    queryFn:  () => isSchoolAdmin ? roleService.school.getPermissions({ page: 1, limit: 100 }) : roleService.getAllPermissions(),
    staleTime: 300_000,
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => isSchoolAdmin ? roleService.school.createRole(data) : roleService.createRole(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  })
}

export function useAssignPermissions() {
  const qc = useQueryClient()
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => isSchoolAdmin ? roleService.school.mapRolePermission(data) : roleService.mapRolePermission(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  })
}

export function useMappedRoles(p?: { page?: number; limit?: number }) {
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  return useQuery({
    queryKey: ['mapped-roles', p, isSchoolAdmin],
    queryFn:  () => isSchoolAdmin ? roleService.school.listMappedRoles(p) : roleService.listMappedRoles(p),
    staleTime: 300_000,
  })
}

// alias
export const useDeleteRole = useCreateRole
